(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const initDrawer = () => {
    const drawer = document.querySelector('[data-vt-drawer]');
    const opener = document.querySelector('[data-vt-drawer-toggle]');
    if (!drawer || !opener) return;
    if (drawer.dataset.vtReady === 'true') return;
    drawer.dataset.vtReady = 'true';

    let previousFocus = null;
    const panel = drawer.querySelector('[data-vt-drawer-panel]') || drawer;
    const backgroundNodes = [...document.body.children].filter((node) => node !== drawer && !node.matches('script'));

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      opener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      backgroundNodes.forEach((node) => { node.inert = false; });
      previousFocus?.focus();
    };

    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const normalizeDrawer = () => {
      if (desktopQuery.matches || drawer.getAttribute('aria-hidden') === 'true') close();
    };

    const open = () => {
      previousFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      backgroundNodes.forEach((node) => { node.inert = true; });
      window.setTimeout(() => panel.querySelector(focusableSelector)?.focus(), 50);
    };

    opener.addEventListener('click', () => drawer.classList.contains('is-open') ? close() : open());
    drawer.querySelectorAll('[data-vt-drawer-close]').forEach((node) => node.addEventListener('click', close));
    desktopQuery.addEventListener?.('change', normalizeDrawer);
    window.addEventListener('pageshow', normalizeDrawer);
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initHeaderNavigation = () => {
    document.querySelectorAll('[data-vt-header-shell]').forEach((shell) => {
      if (shell.dataset.vtHeaderReady === 'true') return;
      shell.dataset.vtHeaderReady = 'true';
      const links = [...document.querySelectorAll('[data-vt-nav-link]')];
      const sectionName = shell.querySelector('[data-vt-section-name]');
      const defaultSectionName = sectionName?.textContent?.trim() || '';
      const currentLocation = new URL(window.location.href);
      const scopedLinks = links.map((link) => {
        const url = new URL(link.href, currentLocation.href);
        const samePage = url.origin === currentLocation.origin && url.pathname.replace(/\/$/, '') === currentLocation.pathname.replace(/\/$/, '');
        const target = samePage && url.hash ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null;
        return { link, target, hash: url.hash };
      }).filter((item) => item.target);
      const targets = [...new Map(scopedLinks.map((item) => [item.hash, item.target])).entries()].map(([hash, target]) => ({ hash, target }));
      let frame = 0;

      const update = () => {
        frame = 0;
        shell.classList.toggle('is-scrolled', window.scrollY > 28);
        const marker = window.scrollY + shell.getBoundingClientRect().height + Math.min(160, window.innerHeight * .18);
        let activeHash = '';
        let hasEnteredSection = false;
        targets.forEach((item) => {
          if (item.target.offsetTop <= marker) {
            activeHash = item.hash;
            hasEnteredSection = true;
          }
        });
        scopedLinks.forEach(({ link, hash }) => {
          const isCurrent = Boolean(activeHash && hash === activeHash);
          link.classList.toggle('is-current', isCurrent);
          if (isCurrent) link.setAttribute('aria-current', 'location');
          else if (link.getAttribute('aria-current') === 'location') link.removeAttribute('aria-current');
        });
        if (sectionName) {
          const activeLink = scopedLinks.find(({ link, hash }) => hash === activeHash && link.closest('.vt-nav, .vt-header__nav'))?.link;
          const label = activeLink?.querySelector('.vt-nav__label, .vt-header__nav-label')?.textContent?.trim();
          sectionName.textContent = hasEnteredSection && label ? label : defaultSectionName;
        }
      };

      const requestUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };
      scopedLinks.forEach(({ link }) => link.addEventListener('click', requestUpdate));
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      window.addEventListener('hashchange', requestUpdate);
      update();
    });
  };

  const initMarketDisclosures = () => {
    document.querySelectorAll('[data-vt-market]').forEach((market) => {
      if (market.dataset.vtMarketReady === 'true') return;
      market.dataset.vtMarketReady = 'true';
      const trigger = market.querySelector('summary');
      market.addEventListener('toggle', () => trigger?.setAttribute('aria-expanded', String(market.open)));
      market.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !market.open) return;
        market.removeAttribute('open');
        trigger?.focus();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!market.open || market.contains(event.target)) return;
        market.removeAttribute('open');
      });
      trigger?.setAttribute('aria-expanded', String(market.open));
    });
  };

  const initLocalization = () => {
    document.querySelectorAll('[data-vt-localization]').forEach((form) => {
      if (form.dataset.vtReady === 'true') return;
      form.dataset.vtReady = 'true';
      form.querySelectorAll('select').forEach((select) => select.addEventListener('change', () => form.requestSubmit()));
    });
  };

  const initWaterline = () => {
    document.querySelectorAll('[data-vt-waterline]').forEach((section) => {
      if (section.dataset.vtReady === 'true') return;
      section.dataset.vtReady = 'true';
      const media = section.querySelector('[data-vt-waterline-media]');
      section.querySelectorAll('[data-vt-waterline-tab]').forEach((button) => {
        button.addEventListener('click', () => {
          section.querySelectorAll('[data-vt-waterline-tab]').forEach((item) => {
            const active = item === button;
            item.setAttribute('aria-current', String(active));
            item.querySelector('button')?.setAttribute('aria-pressed', String(active));
          });
          if (media) media.style.setProperty('--vt-waterline-position', button.dataset.linePosition || '58%');
        });
      });
    });
  };

  const initCarbonViews = () => {
    document.querySelectorAll('[data-vt-carbon]').forEach((section) => {
      if (section.dataset.vtReady === 'true') return;
      section.dataset.vtReady = 'true';
      const buttons = [...section.querySelectorAll('[data-vt-carbon-view]')];
      const images = [...section.querySelectorAll('[data-vt-carbon-image]')];
      const sectionIndex = [...document.querySelectorAll('[data-vt-carbon]')].indexOf(section);

      const selectView = (selectedIndex, immediate = false) => {
        buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === selectedIndex)));
        images.forEach((image, index) => {
          const active = index === selectedIndex;
          window.clearTimeout(image.vtHideTimer);
          image.setAttribute('aria-hidden', String(!active));
          if (active) {
            image.hidden = false;
            if (immediate) image.classList.add('is-active');
            else window.requestAnimationFrame(() => image.classList.add('is-active'));
          } else {
            image.classList.remove('is-active');
            if (immediate) image.hidden = true;
            else image.vtHideTimer = window.setTimeout(() => { image.hidden = true; }, 360);
          }
        });
      };

      images.forEach((image, index) => {
        const imageId = image.id || `VtCarbonImage-${sectionIndex}-${index}`;
        image.id = imageId;
        buttons[index]?.setAttribute('aria-controls', imageId);
      });

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => selectView(index));
      });
      selectView(Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true')), true);
    });
  };

  const initRails = () => {
    document.querySelectorAll('[data-vt-rail]').forEach((track) => {
      if (track.dataset.vtRailReady === 'true') return;
      track.dataset.vtRailReady = 'true';
      const items = [...track.children].filter((item) => item.matches('a, article, [role="listitem"]'));
      const cue = track.nextElementSibling?.matches('.vt-rail-cue') ? track.nextElementSibling : null;
      const current = cue?.querySelector('[data-vt-rail-current]');
      if (!items.length || !cue) return;

      let frame = 0;
      const getActiveIndex = () => {
        const trackLeft = track.getBoundingClientRect().left;
        return items.reduce((bestIndex, item, index) => {
          const itemLeft = item.getBoundingClientRect().left;
          const bestLeft = items[bestIndex].getBoundingClientRect().left;
          return Math.abs(itemLeft - trackLeft) < Math.abs(bestLeft - trackLeft) ? index : bestIndex;
        }, 0);
      };
      const update = () => {
        frame = 0;
        const activeIndex = getActiveIndex();
        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
        cue.style.setProperty('--vt-rail-progress', String((activeIndex + 1) / items.length));
      };
      const requestUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };
      track.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = Math.min(items.length - 1, Math.max(0, getActiveIndex() + direction));
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({ left: items[nextIndex].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      update();
    });
  };

  const initScrollProgress = () => {
    const root = document.documentElement;
    if (root.dataset.vtScrollReady === 'true') return;
    root.dataset.vtScrollReady = 'true';
    let frame = 0;
    const update = () => {
      frame = 0;
      const available = Math.max(1, root.scrollHeight - window.innerHeight);
      root.style.setProperty('--vt-scroll-progress', String(Math.min(1, Math.max(0, window.scrollY / available))));
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  };

  const initPreviewCart = () => {
    const cart = document.querySelector('[data-vt-preview-cart]');
    const opener = document.querySelector('[data-vt-cart-toggle]');
    if (!cart || !opener) return;
    if (cart.dataset.vtReady === 'true') return;
    cart.dataset.vtReady = 'true';
    let previousFocus = null;
    const panel = cart.querySelector('.vt-preview-cart__panel') || cart;
    const backgroundNodes = [...document.body.children].filter((node) => node !== cart && !node.matches('script'));
    const close = () => {
      cart.classList.remove('is-open');
      cart.setAttribute('aria-hidden', 'true');
      opener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      backgroundNodes.forEach((node) => { node.inert = false; });
      previousFocus?.focus();
    };
    const open = () => {
      previousFocus = document.activeElement;
      cart.classList.add('is-open');
      cart.setAttribute('aria-hidden', 'false');
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      backgroundNodes.forEach((node) => { node.inert = true; });
      window.setTimeout(() => panel.querySelector('[data-vt-cart-close]')?.focus(), 50);
    };
    opener.addEventListener('click', open);
    cart.querySelectorAll('[data-vt-cart-close]').forEach((node) => node.addEventListener('click', close));
    cart.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const normalizeConfigurationText = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const sanitizeConfigurationValue = (value = '') => String(value)
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .trim()
    .slice(0, 80);

  const initLiveConsultationPrefill = () => {
    document.querySelectorAll('[data-vt-live-consultation] .vt-consultation__form').forEach((form) => {
      if (form.dataset.vtPrefillReady === 'true') return;
      form.dataset.vtPrefillReady = 'true';

      const params = new URLSearchParams(window.location.search);
      const requestedWatercraft = normalizeConfigurationText(params.get('watercraft') || '');
      const requestedEdition = params.get('edition') || '';
      const requestedFinish = params.get('finish') || '';
      const watercraftMap = {
        jetboard: {
          value: 'Jetboard',
          terms: ['jetboard', 'surfboard', 'electric surfboard', '冲浪板', 'tabla de surf'],
        },
        hydrofoil: {
          value: 'Hydrofoil',
          terms: ['hydrofoil', 'efoil', '水翼板', 'hidroala'],
        },
        'carbon-max': {
          value: 'Carbon Max',
          terms: ['carbon max', '碳纤维', 'carbono max'],
        },
      };
      const watercraft = watercraftMap[requestedWatercraft];
      const edition = sanitizeConfigurationValue(requestedEdition);
      const finish = sanitizeConfigurationValue(requestedFinish);

      const addHiddenField = (name, value) => {
        if (!value) return;
        let input = form.querySelector(`[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.append(input);
        }
        input.defaultValue = value;
        input.value = value;
      };

      if (watercraft) {
        addHiddenField('contact[Watercraft]', watercraft.value);
        const interest = form.querySelector('[name="contact[Product interest]"]');
        const matchingOption = [...(interest?.options || [])].find((option) => {
          const optionText = normalizeConfigurationText(`${option.value} ${option.textContent || ''}`);
          return watercraft.terms.some((term) => optionText.includes(normalizeConfigurationText(term)));
        });
        if (matchingOption) interest.value = matchingOption.value;
      }

      addHiddenField('contact[Edition]', edition);
      addHiddenField('contact[Finish]', finish);

      const context = form.querySelector('[data-vt-live-selection]');
      const selection = [
        watercraft?.value,
        edition && `${context?.dataset.editionLabel || 'Edition'}: ${edition}`,
        finish && `${context?.dataset.finishLabel || 'Finish'}: ${finish}`,
      ].filter(Boolean);
      if (context && selection.length) {
        context.textContent = `${context.dataset.prefix || 'Configuration carried forward'}: ${selection.join(' / ')}.`;
        context.hidden = false;
      }
    });
  };

  const initPreviewForms = () => {
    document.querySelectorAll('[data-vt-preview-form]').forEach((form) => {
      if (form.dataset.vtReady === 'true') return;
      form.dataset.vtReady = 'true';

      const params = new URLSearchParams(window.location.search);
      const watercraft = params.get('watercraft');
      const edition = params.get('edition');
      const finish = params.get('finish');
      if (watercraft === 'jetboard') {
        const interest = form.querySelector('[name="interest"]');
        const matchingOption = [...(interest?.options || [])].find((option) => option.textContent.trim() === 'Electric jetboard');
        if (matchingOption) interest.value = matchingOption.value;
      }
      if (edition) {
        const readableEdition = edition.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
        const hiddenEdition = document.createElement('input');
        hiddenEdition.type = 'hidden';
        hiddenEdition.name = 'edition';
        hiddenEdition.value = readableEdition;
        form.append(hiddenEdition);

      }
      if (finish) {
        const readableFinish = finish.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
        const hiddenFinish = document.createElement('input');
        hiddenFinish.type = 'hidden';
        hiddenFinish.name = 'finish';
        hiddenFinish.value = readableFinish;
        form.append(hiddenFinish);
      }
      if (edition || finish) {
        const readableEdition = edition ? edition.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '';
        const readableFinish = finish ? `${finish.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())} finish` : '';
        const selection = [readableEdition, readableFinish].filter(Boolean).join(' / ');
        const context = document.createElement('p');
        context.className = 'vt-form-context';
        context.textContent = `Configuration carried forward: Jetboard / ${selection}.`;
        form.prepend(context);
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const status = form.querySelector('[data-vt-form-status]');
        if (status) status.hidden = false;
      });
    });
    document.querySelectorAll('[data-vt-preview-localization]').forEach((form) => {
      if (form.dataset.vtReady === 'true') return;
      form.dataset.vtReady = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const country = form.querySelector('[name="country"]');
        const language = form.querySelector('[name="language"]');
        const triggerValue = document.querySelector('[data-vt-market-value]');
        if (triggerValue && country && language) {
          const marketLabel = country.selectedOptions[0]?.textContent?.trim() || 'Market';
          const languageLabel = language.selectedOptions[0]?.textContent?.trim() || 'English';
          const market = marketLabel.split('—')[0]?.trim() || 'Market';
          const locale = language.value === '简体中文' ? '简中' : language.value === 'Español' ? 'ES' : 'EN';
          const marketCode = market === 'United States' ? 'US' : market === 'China mainland' ? 'CN' : market === 'Perú' ? 'PE' : 'SA';
          triggerValue.textContent = `${marketCode} / ${locale}`;
          document.querySelectorAll('[data-vt-preview-market-footer]').forEach((node) => { node.textContent = marketLabel; });
          document.querySelectorAll('[data-vt-preview-language-footer]').forEach((node) => { node.textContent = languageLabel; });
        }
        form.closest('details')?.removeAttribute('open');
        form.closest('[data-vt-drawer]')?.querySelector('[data-vt-drawer-close]')?.click();
      });
    });
    document.querySelectorAll('[data-vt-preview-newsletter]').forEach((form) => {
      if (form.dataset.vtReady === 'true') return;
      form.dataset.vtReady = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const status = form.querySelector('[data-vt-newsletter-status]');
        if (status) status.hidden = false;
      });
    });
  };

  const initEditionPickers = () => {
    document.querySelectorAll('[data-vt-editions]').forEach((section) => {
      if (section.dataset.vtEditionReady === 'true') return;
      section.dataset.vtEditionReady = 'true';
      const options = [...section.querySelectorAll('[data-vt-edition-option]')];
      const selectedName = section.querySelector('[data-vt-edition-name]');
      const selectedPrice = section.querySelector('[data-vt-edition-price]');
      const consultationLink = section.querySelector('[data-vt-edition-link]');
      const finishLinks = [...document.querySelectorAll('[data-vt-finish-link]')];
      if (!options.length) return;

      const selectEdition = (selectedOption) => {
        options.forEach((option) => {
          const selected = option === selectedOption;
          option.classList.toggle('is-selected', selected);
          option.dataset.selected = String(selected);
          option.setAttribute('aria-pressed', String(selected));
          const action = option.querySelector('[data-vt-edition-action]');
          if (action) action.textContent = selected ? 'Current selection' : 'Select edition';
        });

        const name = selectedOption.dataset.editionName || '';
        const price = selectedOption.dataset.editionPrice || '';
        const editionSlug = selectedOption.dataset.editionSlug || '';
        if (selectedName) selectedName.textContent = name;
        if (selectedPrice) selectedPrice.textContent = price;
        if (consultationLink) consultationLink.href = `./consultation.html?watercraft=jetboard&edition=${encodeURIComponent(editionSlug)}`;
        finishLinks.forEach((link) => {
          const finish = link.dataset.finish || '';
          link.href = `./consultation.html?watercraft=jetboard&edition=${encodeURIComponent(editionSlug)}&finish=${encodeURIComponent(finish)}`;
        });
      };

      options.forEach((option) => option.addEventListener('click', () => selectEdition(option)));
      selectEdition(options.find((option) => option.getAttribute('aria-pressed') === 'true') || options[0]);
    });
  };

  const initLiveProductConfiguration = () => {
    const configurationValue = (value = '') => String(value ?? '');
    const parseMap = (section, selector) => {
      try {
        return JSON.parse(section.querySelector(selector)?.textContent || '[]');
      } catch (_error) {
        return [];
      }
    };
    const matchesProduct = (section, variant) => {
      const productId = section.dataset.vtProductId || '';
      return !(variant?.product_id && productId && String(variant.product_id) !== productId);
    };
    const getSelectionSnapshot = (target) => {
      const variantSelects = target?.closest?.('variant-selects');
      if (!variantSelects) return null;

      const selectedOptions = typeof variantSelects.getAllSelectedOptions === 'function'
        ? variantSelects.getAllSelectedOptions()
        : [];
      const options = selectedOptions.map(({ value }) => configurationValue(value));
      if (!options.length) return null;

      return {
        product_id: variantSelects.dataset.productId || '',
        options,
      };
    };

    const updateConsultationCtas = (variant) => {
      const allowedWatercraft = new Set(['jetboard', 'hydrofoil', 'carbon-max']);
      document.querySelectorAll('[data-vt-product-consultation]').forEach((link) => {
        const productId = link.dataset.vtProductId || '';
        if (variant?.product_id && productId && String(variant.product_id) !== productId) return;

        const productInfo = link.closest('product-info');
        const watercraft = link.dataset.vtWatercraft || '';
        const url = new URL(link.href, window.location.origin);
        const editionIndex = Number.parseInt(link.dataset.vtEditionOptionIndex || '-1', 10);
        const finishIndex = Number.parseInt(link.dataset.vtFinishOptionIndex || '-1', 10);
        const options = variant?.options || [];

        if (allowedWatercraft.has(watercraft)) {
          url.searchParams.set('watercraft', watercraft);
          if (editionIndex >= 0 && options[editionIndex]) {
            url.searchParams.set('edition', configurationValue(options[editionIndex]));
          } else if (editionIndex >= 0) {
            url.searchParams.delete('edition');
          }
          if (finishIndex >= 0 && options[finishIndex]) {
            url.searchParams.set('finish', configurationValue(options[finishIndex]));
          } else if (finishIndex >= 0) {
            url.searchParams.delete('finish');
          }
        } else {
          url.searchParams.delete('watercraft');
          url.searchParams.delete('edition');
          url.searchParams.delete('finish');
        }
        link.href = url.toString();

        const hasPrice = variant?.price !== undefined && variant?.price !== null;
        if (!hasPrice || !productInfo) return;
        const consultationMode = productInfo.dataset.vtConsultationMode === 'true' || Number(variant.price) <= 0;
        const panel = link.closest('[data-vt-consultation-panel]');
        if (panel) panel.hidden = !consultationMode;
        productInfo.querySelectorAll('[data-vt-transaction-control]').forEach((control) => {
          control.hidden = consultationMode;
        });
        if (consultationMode) {
          const submitButton = productInfo.querySelector('[name="add"]');
          if (submitButton) submitButton.disabled = true;
        }
      });
    };

    const updateLineups = (variant) => {
      document.querySelectorAll('[data-vt-live-jetboard-lineup]').forEach((section) => {
        if (!matchesProduct(section, variant)) return;
        const optionIndex = Number.parseInt(section.dataset.vtEditionOptionIndex || '-1', 10);
        const editionValue = optionIndex >= 0 ? variant?.options?.[optionIndex] : '';
        if (!editionValue) return;
        section.querySelectorAll('[data-vt-auto-config-link]').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('edition', editionValue);
          link.href = url.toString();
        });

        const mediaMap = parseMap(section, '[data-vt-jetboard-media-map]');
        section.querySelectorAll('[data-vt-auto-variant-media]').forEach((media) => {
          const finishValue = configurationValue(media.dataset.vtFinish);
          const match = mediaMap.find((entry) => (
            configurationValue(entry.edition) === configurationValue(editionValue)
            && configurationValue(entry.finish) === finishValue
          )) || mediaMap.find((entry) => configurationValue(entry.finish) === finishValue);
          const image = media.querySelector('img');
          if (!match?.src || !image) return;
          image.removeAttribute('srcset');
          image.src = match.src;
          if (match.alt) image.alt = match.alt;
        });
      });
    };

    const updateEditionRows = (variant) => {
      document.querySelectorAll('[data-vt-live-editions]').forEach((section) => {
        if (!matchesProduct(section, variant)) return;
        const optionIndex = Number.parseInt(section.dataset.vtEditionOptionIndex || '-1', 10);
        if (optionIndex < 0 || !variant?.options?.length) return;
        const variantMap = parseMap(section, '[data-vt-edition-variant-map]');
        if (!variantMap.length) return;
        const consultationMode = section.dataset.vtConsultationMode === 'true';
        const selectedEditionValue = configurationValue(variant.options[optionIndex]);

        section.querySelectorAll('[data-vt-edition-row]').forEach((row) => {
          const editionValue = configurationValue(row.dataset.vtEditionValue);
          const match = variantMap.find((entry) => entry.options?.every((option, index) => (
            configurationValue(option) === configurationValue(index === optionIndex ? editionValue : variant.options[index])
          )));
          const exists = Boolean(match);
          const selectable = consultationMode
            ? Boolean(match?.configurable)
            : Boolean(match?.purchasable);
          const selected = editionValue === selectedEditionValue;
          const unavailableLabel = exists
            ? section.dataset.vtUnavailableLabel
            : section.dataset.vtCombinationUnavailableLabel;

          row.classList.toggle('is-selected', selected);
          row.classList.toggle('is-unavailable', !selectable);
          row.dataset.selected = String(selected);
          row.dataset.purchasable = String(Boolean(match?.purchasable));
          if (selected) row.setAttribute('aria-current', 'page');
          else row.removeAttribute('aria-current');
          if (selectable) {
            row.href = match.url;
            row.removeAttribute('aria-disabled');
          } else {
            row.removeAttribute('href');
            row.setAttribute('aria-disabled', 'true');
          }

          const action = row.querySelector('.vt-edition-row__action');
          const status = row.querySelector('.vt-edition-row__status');
          if (action) {
            action.textContent = selected
              ? section.dataset.vtSelectedLabel
              : selectable ? section.dataset.vtSelectLabel : unavailableLabel;
          }
          if (status) {
            status.textContent = selectable
              ? consultationMode ? section.dataset.vtConsultationLabel : section.dataset.vtAvailableLabel
              : unavailableLabel;
          }

          const price = row.querySelector('.vt-edition-row__price');
          const strong = price?.querySelector('strong');
          let reference = price?.querySelector('small');
          let compare = price?.querySelector('s');
          const displayPrice = consultationMode ? match?.peruReference : match?.price;
          if (strong) {
            strong.textContent = exists && displayPrice ? displayPrice : '—';
            if (exists && displayPrice) strong.removeAttribute('aria-hidden');
            else strong.setAttribute('aria-hidden', 'true');
          }
          if (consultationMode && match?.peruReference) {
            if (!reference && price) {
              reference = document.createElement('small');
              price.insertBefore(reference, strong);
            }
            reference.textContent = section.dataset.vtReferenceLabel;
          } else {
            reference?.remove();
          }
          if (!consultationMode && match?.comparePrice) {
            if (!compare && price) {
              compare = document.createElement('s');
              price.insertBefore(compare, strong);
            }
            compare.textContent = match.comparePrice;
          } else {
            compare?.remove();
          }
        });
      });
    };

    if (!document.querySelector('[data-vt-live-jetboard-lineup], [data-vt-live-editions], [data-vt-product-consultation]')) return;
    if (document.body.dataset.vtJetboardVariantReady === 'true') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    document.body.dataset.vtJetboardVariantReady = 'true';
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
      const selection = getSelectionSnapshot(event?.data?.target);
      if (!selection) return;
      updateLineups(selection);
      updateEditionRows(selection);
      updateConsultationCtas(selection);
    });
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      updateLineups(event?.data?.variant);
      updateEditionRows(event?.data?.variant);
      updateConsultationCtas(event?.data?.variant);
    });
  };

  const initPreviewProducts = () => {
    document.querySelectorAll('[data-vt-product-preview]').forEach((section) => {
      if (section.dataset.vtPreviewProductReady === 'true') return;
      section.dataset.vtPreviewProductReady = 'true';
      const images = [...section.querySelectorAll('[data-vt-preview-product-image]')];
      const buttons = [...section.querySelectorAll('[data-vt-preview-product-view]')];

      const selectImage = (selectedButton) => {
        const selectedId = selectedButton.getAttribute('aria-controls');
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button === selectedButton)));
        images.forEach((image) => {
          const active = image.id === selectedId;
          image.hidden = !active;
          image.setAttribute('aria-hidden', String(!active));
          image.classList.toggle('is-active', active);
        });
      };

      buttons.forEach((button) => button.addEventListener('click', () => selectImage(button)));
      const initiallySelected = buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
      if (initiallySelected) selectImage(initiallySelected);

      section.querySelectorAll('[data-vt-preview-quantity]').forEach((control) => {
        const input = control.querySelector('input[type="number"]');
        if (!input) return;
        control.querySelectorAll('[data-vt-quantity-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const step = Number.parseInt(button.dataset.vtQuantityStep || '0', 10);
            const minimum = Number.parseInt(input.min || '1', 10);
            const current = Number.parseInt(input.value || String(minimum), 10);
            input.value = String(Math.max(minimum, current + step));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      });
    });
  };

  const initReveals = () => {
    const nodes = [...document.querySelectorAll('.vt-reveal:not([data-vt-reveal-ready])')];
    nodes.forEach((node) => { node.dataset.vtRevealReady = 'true'; });
    if (!nodes.length || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach((node) => observer.observe(node));
  };

  const initAll = () => {
    initDrawer();
    initHeaderNavigation();
    initMarketDisclosures();
    initLocalization();
    initWaterline();
    initCarbonViews();
    initRails();
    initScrollProgress();
    initPreviewCart();
    initLiveConsultationPrefill();
    initPreviewForms();
    initEditionPickers();
    initLiveProductConfiguration();
    initPreviewProducts();
    initReveals();
  };

  onReady(initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
